import React, { useRef } from 'react';
import classnames from 'classnames';
import GlobalProps from '../globalProps';
import { SeatmapMap } from './SeatmapMap';
import { Seatmap as SeatmapModel } from '../../model/seatmap/seatmap';
import './Seatmap.css';
import { ZoomControl } from './ZoomControl';
import { AnimatedHeight } from './../AnimatedHeight/AnimatedHeight';
import { QuickNav } from './QuickNav';
import { Button } from '../Button/Button';
import { t } from '../../locales/translate';
import { allSeatStatuses } from '../../model/contingent';
import { compact, kebabCase } from 'lodash';
import { Map, MapElement } from './Map';
import { Card } from '../Card/Card';
import { Icon } from '../Icon/Icon';
import { SeatmapMode, Section } from '../../model/seatmap/types';
import { InfoOverlay } from './InfoOverlay';
import { boundsOf, centerOf, seatName, useSeatmap } from '../../model/seatmap/helper';

interface ChildOptions {
  seatmap: SeatmapModel;
}

interface Props extends GlobalProps {
  layoutId: string;
  eventId?: string;
  mode: SeatmapMode;
  children?: (context: ChildOptions) => React.ReactNode;
}

export function Seatmap({ className, style, children, layoutId, mode, eventId }: Props): JSX.Element {
  const mapRef = useRef<MapElement | null>();
  const seatmap = useSeatmap({ id: layoutId, eventId });

  function navigateTo(section: Section): void {
    const bounds = boundsOf(section.hullPoints);
    const factor = (seatmap.layout.boundingBox.height * 0.7) / bounds.height;
    mapRef.current?.zoomTo(centerOf(section.hullPoints), factor);
  }

  const statusList = new Set(compact(seatmap.layout.sections.flatMap((e) => e.seats.map((e) => e.allocation?.status))));

  const overlaySeat =
    seatmap.selection.hoveredSeat ?? (seatmap.selection.type === 'seats' ? seatmap.selection.lastSelectedSeat : null);
  const overlaySection = seatmap.selection.hoveredGeneralAdmissionSection;

  return (
    <div
      className={classnames('seatmap', className, {
        'seatmap--loading': seatmap.loading,
      })}
      style={style}
    >
      <Map ref={mapRef} virtualCoordinates={seatmap.layout.boundingBox}>
        <SeatmapMap seatmap={seatmap} mode={mode} />
      </Map>
      <div className="seatmap__controls">
        <ZoomControl onZoomIn={() => mapRef.current?.zoomBy(1.5)} onZoomOut={() => mapRef.current?.zoomBy(0.7)} />
        <Button
          elevated
          icon="center--sm"
          onClick={() => {
            mapRef.current?.reset();
          }}
        />
        {seatmap.selection.lastSelectedSeat && seatmap.selection.type === 'seats' && (
          <Button
            elevated
            icon="arrow-sortable"
            className="seatmap__button-select-row"
            ariaLabel={t.model.venueLayout.addRowToSelection}
            onClick={() => seatmap.selection.addSeatsInRowToSelection()}
          />
        )}
        {seatmap.selection.lastSelectedSeat && seatmap.selection.type === 'seats' && (
          <Button
            elevated
            icon="expand"
            ariaLabel={t.model.venueLayout.addSectionToSelection}
            onClick={() => {
              if (seatmap.selection.lastSelectedSeat) {
                seatmap.selection.addSeatsInSectionToSelection(seatmap.selection.lastSelectedSeat.section);
              }
            }}
          />
        )}
      </div>
      <div className="seatmap__additional-controls">
        <Card
          size="xs"
          title={(overlaySeat && seatName(overlaySeat)) ?? overlaySection?.name ?? t.model.venueLayout.quickNav}
          actions={
            overlaySeat || overlaySection ? (
              <Icon
                name="remove"
                onClick={() => {
                  seatmap.selection.clear();
                }}
              />
            ) : undefined
          }
        >
          <AnimatedHeight minHeight="52px">
            {overlaySeat || overlaySection ? (
              <InfoOverlay eventId={eventId} seat={overlaySeat} section={overlaySection} />
            ) : (
              seatmap.layout.sections.length > 0 && (
                <QuickNav sections={seatmap.layout.sections} onChange={(section) => navigateTo(section)} />
              )
            )}
          </AnimatedHeight>
        </Card>
        {children?.({ seatmap })}
      </div>
      <div className="seatmap__legend">
        {allSeatStatuses
          .filter((e) => statusList.has(e))
          .map((status) => (
            <div className={`seatmap__legend-title seatmap__legend-title--${kebabCase(status)}`} key={status}>
              {t.model.status[status]}
            </div>
          ))}
      </div>
    </div>
  );
}
